// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The single-cycle data layer: get_reporting_cycle(_cycle_id) RETURNS SETOF
// reporting_cycles — the full row incl. the canonical `ir` jsonb the report/flags/gate tabs
// render. Returns the first (only) row and surfaces the typed IR under `.ir`.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { CycleStage, CycleStatus, IR } from "@/types/ir";

type Row = Record<string, any>;

// The reporting_cycles row shape (0001) — permissive, with the strongly-typed IR overlaid.
export interface ReportingCycleRow extends Row {
  id?: string;
  period_type?: string;
  period_label?: string;
  as_of_date?: string;
  is_webinar_month?: boolean;
  status?: CycleStatus;
  current_stage?: CycleStage;
  stage_cap?: number;
  params?: Record<string, any>;
  ir?: IR | null;
  gate_approved?: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
  last_error?: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * @param live  Poll on the same 5s beat as useReportingCycleRun. Pass true while a run is in
 *   flight. WITHOUT it the 2-minute staleTime below means the stage cursor on screen can be
 *   two minutes behind the worker, so the page shows a spinner over frozen text and reads as
 *   hung. The run row and the cycle row describe the same event; they must refresh together
 *   or the faster one is just decoration.
 */
export function useReportingCycle(cycleId: string | undefined, live = false) {
  return useQuery({
    queryKey: ["get_reporting_cycle", cycleId],
    enabled: !!cycleId,
    // Live: never serve a cached cursor, and re-read every 5s. Idle: a parked cycle is a
    // static fact and polling it costs a round trip per viewer for nothing.
    refetchInterval: live ? 5000 : false,
    staleTime: live ? 0 : 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_reporting_cycle", { _cycle_id: cycleId });
      if (error) throw error;
      const rows = (data as ReportingCycleRow[]) ?? [];
      return (rows[0] ?? null) as ReportingCycleRow | null;
    },
  });
}
